import { observer } from 'mobx-react';
import PropTypes from 'prop-types';
import SpotlightChip from './SpotlightChip';
import { SPOTLIGHT_DEFINITIONS } from '../constants/spotlightDefinitions';

/**
 * Spotlights bar — visible only on the List view, rendered inline with the
 * existing toolbar buttons (Adjust search · Customize columns · Save search ·
 * view toggle) inside `.filters-download-container`.
 *
 * Renders the three locked V1 one-click smart-cut chips with live counts.
 * Click toggles a Spotlight on/off; multiple active compound as AND.
 *
 * Counts come from `state.spotlightCounts` (computed in index.jsx) and reflect
 * "how many companies would match this Spotlight in addition to the current
 * column-filter set," updating reactively as filters change.
 *
 * The "+ Custom" placeholder was removed in V1.1 — that affordance returns
 * with V2's saved-Spotlights feature.
 */
const SpotlightsBar = observer(
  ({
    activeSpotlights,
    spotlightCounts = {},
    onToggleSpotlight = () => {},
    excludedSpotlightIds = null
  }) => {
    const visibleDefs = excludedSpotlightIds
      ? SPOTLIGHT_DEFINITIONS.filter((def) => !excludedSpotlightIds.has?.(def.id))
      : SPOTLIGHT_DEFINITIONS;

    return (
      <div className="spotlights-bar">
        <span className="spotlights-bar-label">Select</span>
        {visibleDefs.map((def) => (
          <SpotlightChip
            key={def.id}
            definition={def}
            count={spotlightCounts[def.id] || 0}
            active={activeSpotlights?.has?.(def.id) || false}
            onClick={() => onToggleSpotlight(def.id)}
          />
        ))}
      </div>
    );
  }
);

SpotlightsBar.propTypes = {
  /** MobX-observable Set of currently active Spotlight IDs. */
  activeSpotlights: PropTypes.object,
  /** Map: Spotlight ID → live count (computed in parent). */
  spotlightCounts: PropTypes.object,
  /** Toggle handler — receives Spotlight ID. */
  onToggleSpotlight: PropTypes.func,
  /**
   * Optional Set of Spotlight IDs to hide from the bar. Used to suppress
   * Spotlights whose predicates don't apply in the current context — e.g.
   * "High matches" reads the AI-rationale badge which only appears on
   * Characteristics searches, never on Industry searches, so we hide it
   * rather than showing a permanent "0".
   */
  excludedSpotlightIds: PropTypes.object
};

export default SpotlightsBar;
